import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ActivityEventDto, type ActivityResponse, type ActivityVerb } from '@familyhub/shared';
import { api, isOffline } from '../api/client';
import { ProductThumb } from '../components/ProductThumb';
import { useRefreshOnReturn } from '../useRefreshOnReturn';
import { groupActivity, describeCard, type ActivityCard } from '../activity/grouping';
import { Loading } from '../components/Loading';

/**
 * The activity feed (ARCHITECTURE §6, API §9.3).
 *
 * What changed on the shared list, by whom, grouped into a card per person per
 * sitting — the same facts a push notification carries, kept as something you
 * can scroll back through. Two people who shop from one list want to see that
 * the other took the butter off before they buy a second one.
 *
 * Server-derived and fetched on mount: this is a screen read on the sofa, not in
 * a shop, so it does not join the offline core. With no signal it says so and
 * offers nothing false.
 */

/** A stable colour per person, so the two members read apart at a glance. */
function hueFor(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 360_000;
  return hash % 360;
}

function initialOf(name: string): string {
  for (const char of name) {
    if (/\p{L}|\p{N}/u.test(char)) return char.toLocaleUpperCase('nl-NL');
  }
  return '?';
}

/** "Vandaag" / "Gisteren" / "Woensdag — 12 augustus", in the household's day. */
function dateHeader(iso: string): string {
  const at = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (at.toDateString() === today.toDateString()) return 'Vandaag';
  if (at.toDateString() === yesterday.toDateString()) return 'Gisteren';
  const weekday = new Intl.DateTimeFormat('nl-NL', { weekday: 'long' }).format(at);
  const date = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' }).format(at);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} — ${date}`;
}

/**
 * The Dutch a person reads for each verb. "gekocht" matches the rest of the app
 * — the check-off confirmation says "… gekocht" and the add screen has a "Recent
 * gekocht" shelf — so a checked-off line reads the same here as everywhere else.
 */
const VERB_LABEL: Record<ActivityVerb, string> = {
  added: 'toegevoegd',
  bought: 'gekocht',
  removed: 'verwijderd',
};

/** "Sanne heeft 2 artikelen toegevoegd." */
function summary(card: ActivityCard): string {
  const n = card.events.length;
  const noun = n === 1 ? 'artikel' : 'artikelen';
  return `${card.actor.display_name} heeft ${n} ${noun} ${VERB_LABEL[card.verb]}`;
}

export function ActivityScreen() {
  const [events, setEvents] = useState<ActivityEventDto[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function load() {
    try {
      setEvents((await api<ActivityResponse>('/api/v1/activity')).events);
      setProblem(null);
    } catch (err) {
      setProblem(
        isOffline(err)
          ? 'Voor activiteit is verbinding nodig.'
          : 'Kon de activiteit niet laden.',
      );
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // A PWA is left open for hours; what is on screen is a snapshot from whenever
  // it was opened — refresh on the same triggers the sync loop uses.
  useRefreshOnReturn(load);

  const cards = events ? groupActivity(events) : [];

  return (
    <>
      <header className="topbar">
        <Link className="back btn btn-quiet" to="/boodschappen">
          ‹ Terug
        </Link>
        <h1>Activiteit</h1>
      </header>

      <main className="stack">
        {problem && <p className="banner">{problem}</p>}

        {events && events.length === 0 && !problem && (
          <p className="empty">
            Nog geen activiteit.
            <br />
            Wat jij en je huisgenoot op de lijst zetten of eraf halen, verschijnt hier.
          </p>
        )}

        {cards.map((card) => {
          const { source, rows } = describeCard(card);
          return (
            <article className="card activity-card" key={card.key}>
              <div className="activity-head">
                <span
                  className="activity-avatar"
                  aria-hidden="true"
                  style={{
                    backgroundColor: `hsl(${hueFor(card.actor.display_name)} 45% 88%)`,
                    color: `hsl(${hueFor(card.actor.display_name)} 45% 28%)`,
                  }}
                >
                  {initialOf(card.actor.display_name)}
                </span>
                <span className="activity-head-text">
                  <span className="activity-date">{dateHeader(card.at)}</span>
                  <span className="activity-summary">{summary(card)}</span>
                  {source && (
                    <span className="activity-source">
                      uit <span className="activity-source-name">{source}</span>
                    </span>
                  )}
                </span>
              </div>

              <ul className={`activity-items activity-items-${card.verb}`}>
                {rows.map(({ event, meta }) => (
                  <li className="activity-item" key={event.event_key}>
                    <ProductThumb src={event.product.image_path} name={event.product.name} />
                    <span className="activity-item-body">
                      <span className="activity-item-line">
                        <span className="activity-item-name">{event.product.name}</span>
                        {event.quantity > 1 && (
                          <span className="badge activity-qty">{event.quantity}</span>
                        )}
                      </span>
                      {meta && <span className="activity-item-meta">{meta}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}

        {!events && !problem && <Loading />}
      </main>
    </>
  );
}
